(() => {
  'use strict';

  const GLOBAL_NAME = 'WMSetBuilderV8';
  const SET_CART_ATTRIBUTE = 'wm_set_builder__';

  if (window[GLOBAL_NAME]) {
    window[GLOBAL_NAME].initAll();
    return;
  }

  const instances = new WeakMap();
  const activeInstances = new Set();

  const toInteger = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number) : fallback;
  };

  const normalizeCode = (value) => String(value || '').trim().toUpperCase();

  class SetBuilder {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.selected = new Map();
      this.products = new Map();
      this.cart = null;
      this.busy = false;
      this.redirecting = false;
      this.discountSyncing = false;
      this.discountSyncTimer = 0;
      this.ignoreCartEventsUntil = 0;
      this.cartDomObserver = null;
      this.cartRequestObserver = null;
      this.summaryObserver = null;
      this.summaryPassed = false;
      this.stickyBar = null;
      this.stickySubmit = null;
      this.stickyCount = null;
      this.stickySaving = null;
      this.stickyTotal = null;
      this.tierRail = null;
      this.config = this.readConfig();
      this.tiers = this.readTiers();
      this.elements = this.collectElements();

      this.createPremiumElements();
      this.collectProducts();
      this.bindEvents();
      this.renderSelected();
      this.observeSummaryPosition();
      this.refreshCart();
    }

    readConfig() {
      const element = this.root.querySelector('[data-wm-set-config]');

      if (!element) {
        return {};
      }

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        return {};
      }
    }

    readTiers() {
      const tiers = Array.isArray(this.config.tiers) ? this.config.tiers : [];

      return tiers
        .map((tier) => ({
          key: String(tier.key || ''),
          name: String(tier.name || 'Setvorteil'),
          minimum: Math.max(1, toInteger(tier.minimum, 2)),
          cartMinimum: Math.max(1, toInteger(tier.cartMinimum, 3)),
          percent: Math.max(0, Number(tier.percent) || 0),
          code: String(tier.code || '').trim()
        }))
        .sort((first, second) => first.minimum - second.minimum);
    }

    collectElements() {
      return {
        selectionShell: this.root.querySelector('[data-wm-selection-shell]'),
        selectionList: this.root.querySelector('[data-wm-selected-list]'),
        selectionCount: this.root.querySelector('[data-wm-selected-count]'),
        selectionHint: this.root.querySelector('.wm-set-builder__selection-head > p'),
        tierName: this.root.querySelector('[data-wm-tier-name]'),
        subtotal: this.root.querySelector('[data-wm-subtotal]'),
        saving: this.root.querySelector('[data-wm-saving]'),
        total: this.root.querySelector('[data-wm-total]'),
        totalLabel: this.root.querySelector('[data-wm-total-label]')
          || this.root.querySelector('.wm-set-builder__summary-total > span'),
        summary: this.root.querySelector('.wm-set-builder__summary'),
        summaryInner: this.root.querySelector('.wm-set-builder__summary-inner'),
        progress: this.root.querySelector('[data-wm-progress]'),
        cartNote: this.root.querySelector('[data-wm-cart-note]'),
        submit: this.root.querySelector('[data-wm-submit]'),
        buttonLabel: this.root.querySelector('[data-wm-button-label]'),
        spinner: this.root.querySelector('[data-wm-spinner]'),
        fineprint: this.root.querySelector('[data-wm-fineprint]')
          || this.root.querySelector('.wm-set-builder__fineprint'),
        status: this.root.querySelector('[data-wm-status]')
      };
    }

    createPremiumElements() {
      this.createTierRail();
      this.createStickyBar();
    }

    createTierRail() {
      if (!this.elements.summaryInner || !this.tiers.length) {
        return;
      }

      const existingRail = this.elements.summaryInner.querySelector('[data-wm-tier-rail]');

      if (existingRail) {
        existingRail.remove();
      }

      const rail = document.createElement('div');
      rail.className = 'wm-set-builder__tier-rail';
      rail.dataset.wmTierRail = '';
      rail.setAttribute('aria-label', 'Setvorteile nach Produktanzahl');

      this.tiers.forEach((tier, index) => {
        const step = document.createElement('div');
        step.className = 'wm-set-builder__tier-step';
        step.dataset.wmTierMinimum = String(tier.minimum);

        const marker = document.createElement('span');
        marker.className = 'wm-set-builder__tier-marker';
        marker.textContent = String(tier.minimum);
        marker.setAttribute('aria-hidden', 'true');

        const label = document.createElement('span');
        label.className = 'wm-set-builder__tier-label';
        label.textContent = index === this.tiers.length - 1
          ? `${tier.minimum}+ Produkte`
          : `${tier.minimum} Produkte`;

        step.append(marker, label);
        rail.append(step);
      });

      this.elements.summaryInner.append(rail);
      this.tierRail = rail;
    }

    createStickyBar() {
      const owner = String(
        this.config.sectionId
        || this.root.dataset.sectionId
        || 'set-builder'
      );

      document.querySelectorAll('[data-wm-sticky-owner]').forEach((element) => {
        if (element.dataset.wmStickyOwner === owner) {
          element.remove();
        }
      });

      const bar = document.createElement('div');
      bar.className = 'wm-set-builder__sticky-order';
      bar.dataset.wmStickyOwner = owner;
      bar.setAttribute('aria-hidden', 'true');

      const information = document.createElement('div');
      information.className = 'wm-set-builder__sticky-information';

      const count = document.createElement('span');
      count.className = 'wm-set-builder__sticky-count';
      count.textContent = '0 Produkte';

      const saving = document.createElement('span');
      saving.className = 'wm-set-builder__sticky-saving';
      saving.textContent = '0,00 € Vorteil';

      const total = document.createElement('strong');
      total.className = 'wm-set-builder__sticky-total';
      total.textContent = this.formatMoney(0);

      information.append(count, saving, total);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wm-set-builder__sticky-submit';
      button.textContent = 'SET BESTELLEN';
      button.disabled = true;

      bar.append(information, button);
      document.body.append(bar);

      this.stickyBar = bar;
      this.stickySubmit = button;
      this.stickyCount = count;
      this.stickySaving = saving;
      this.stickyTotal = total;
    }

    createSelectedBadge(state) {
      const imageWrap = state.card.querySelector('.wm-set-builder__image-wrap');

      if (!imageWrap || imageWrap.querySelector('[data-wm-selected-badge]')) {
        return;
      }

      const badge = document.createElement('span');
      badge.className = 'wm-set-builder__selected-badge';
      badge.dataset.wmSelectedBadge = '';
      badge.textContent = 'IM SET';
      badge.setAttribute('aria-hidden', 'true');
      imageWrap.append(badge);
    }

    collectProducts() {
      const cards = this.root.querySelectorAll('[data-wm-product-card]');

      cards.forEach((card) => {
        const dataElement = card.querySelector('[data-wm-product-data]');
        const checkbox = card.querySelector('[data-wm-select]');

        if (!dataElement || !checkbox) {
          return;
        }

        let data;

        try {
          data = JSON.parse(dataElement.textContent);
        } catch (error) {
          return;
        }

        const variants = Array.isArray(data.variants)
          ? data.variants.map((variant) => ({
              id: toInteger(variant.id),
              title: String(variant.title || ''),
              available: Boolean(variant.available),
              price: Math.max(0, toInteger(variant.price)),
              compareAtPrice: Math.max(0, toInteger(variant.compareAtPrice)),
              image: variant.image ? String(variant.image) : '',
              imageAlt: String(variant.imageAlt || data.title || '')
            }))
          : [];

        if (!variants.length) {
          return;
        }

        const state = {
          id: String(data.id),
          title: String(data.title || ''),
          url: String(data.url || '#'),
          variants,
          card,
          checkbox,
          select: card.querySelector('[data-wm-variant-select]'),
          image: card.querySelector('.wm-set-builder__product-image'),
          price: card.querySelector('[data-wm-card-price]'),
          compare: card.querySelector('[data-wm-card-compare]'),
          checkText: card.querySelector('[data-wm-check-text]'),
          checkLabel: card.querySelector('.wm-set-builder__check-label'),
          checkBox: card.querySelector('.wm-set-builder__check-box'),
          checkIcon: card.querySelector('.wm-set-builder__check-box svg'),
          quantity: 1,
          cardQuantityControls: null,
          cardQuantityValue: null
        };

        this.createCardQuantityControls(state);
        this.createSelectedBadge(state);
        this.forceCardColors(state);
        this.products.set(state.id, state);
        this.updateCard(state);
      });

      if (this.elements.selectionHint) {
        this.elements.selectionHint.textContent = 'Menge mit − / + ändern.';
      }
    }

    createCardQuantityControls(state) {
      if (!state.checkLabel) {
        return;
      }

      const controls = document.createElement('div');
      controls.className = 'wm-set-builder__card-quantity';
      controls.dataset.wmCardQuantity = '';
      controls.hidden = true;

      const label = document.createElement('span');
      label.className = 'wm-set-builder__quantity-label';
      label.textContent = 'Stückzahl';

      const stepper = this.createQuantityStepper(state, 'card');
      controls.append(label, stepper.element);
      state.checkLabel.insertAdjacentElement('afterend', controls);
      state.cardQuantityControls = controls;
      state.cardQuantityValue = stepper.value;
    }

    bindEvents() {
      const signal = this.controller.signal;

      this.root.addEventListener('change', (event) => this.handleChange(event), { signal });
      this.root.addEventListener('click', (event) => this.handleClick(event), { signal });

      if (this.elements.submit) {
        this.elements.submit.addEventListener('click', () => this.submit(), { signal });
      }

      if (this.stickySubmit) {
        this.stickySubmit.addEventListener('click', () => {
          if (!this.busy && this.getSelectedQuantityCount() > 0) {
            this.submit();
          }
        }, { signal });
      }

      window.addEventListener('pageshow', () => this.refreshCart(), { signal });
      window.addEventListener('focus', () => this.refreshCart(), { signal });
      window.addEventListener('resize', () => this.updateSummaryPosition(), { signal });

      ['cart:update', 'cart:refresh', 'cart:item-added', 'cart:items-updated'].forEach((eventName) => {
        document.addEventListener(eventName, () => this.scheduleDiscountReconciliation(), { signal });
      });

      document.addEventListener('click', (event) => this.handleExternalCartInteraction(event), {
        signal,
        capture: true
      });

      document.addEventListener('change', (event) => this.handleExternalCartInteraction(event), {
        signal,
        capture: true
      });

      document.addEventListener('input', (event) => this.handleExternalCartInteraction(event), {
        signal,
        capture: true
      });

      this.observeCartDrawer();
      this.observeCartRequests();
    }

    observeSummaryPosition() {
      const summary = this.elements.summary;

      if (!summary) {
        return;
      }

      if ('IntersectionObserver' in window) {
        this.summaryObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];

          if (!entry) {
            return;
          }

          this.summaryPassed = !entry.isIntersecting
            && entry.boundingClientRect.bottom <= 0;

          this.updateStickyVisibility();
        }, {
          threshold: [0, 0.08, 0.5]
        });

        this.summaryObserver.observe(summary);
        return;
      }

      window.addEventListener('scroll', () => this.updateSummaryPosition(), {
        signal: this.controller.signal,
        passive: true
      });

      this.updateSummaryPosition();
    }

    updateSummaryPosition() {
      if (!this.elements.summary) {
        return;
      }

      const rectangle = this.elements.summary.getBoundingClientRect();
      this.summaryPassed = rectangle.bottom <= 0;
      this.updateStickyVisibility();
    }

    cartDrawerIsOpen() {
      const selector = '#ea-sticky-cart-root, .ea-sticky-cart, cart-drawer, #CartDrawer, .cart-drawer';
      const directOpenSelector = '[open], .is-open, .is-active, .active, .open, [aria-hidden="false"], [data-open="true"]';
      const nestedOpenSelector = 'dialog[open], cart-drawer[open], #CartDrawer.is-open, .cart-drawer.is-open, .cart-drawer.is-active, .ea-sticky-cart.is-open';

      return Array.from(document.querySelectorAll(selector)).some((drawer) => {
        if (
          !(drawer instanceof HTMLElement)
          || drawer.hidden
          || drawer.getAttribute('aria-hidden') === 'true'
        ) {
          return false;
        }

        const hasOpenState = drawer.matches(directOpenSelector)
          || Boolean(drawer.querySelector(nestedOpenSelector));

        if (!hasOpenState) {
          return false;
        }

        const style = window.getComputedStyle(drawer);
        const rectangle = drawer.getBoundingClientRect();

        const intersectsViewport = rectangle.right > 0
          && rectangle.bottom > 0
          && rectangle.left < window.innerWidth
          && rectangle.top < window.innerHeight;

        const hasDrawerSize = rectangle.width >= Math.min(260, window.innerWidth * 0.55)
          && rectangle.height >= Math.min(260, window.innerHeight * 0.45);

        return style.display !== 'none'
          && style.visibility !== 'hidden'
          && Number.parseFloat(style.opacity || '1') > 0
          && intersectsViewport
          && hasDrawerSize;
      });
    }

    updateStickyVisibility() {
      if (!this.stickyBar) {
        return;
      }

      const shouldShow = this.summaryPassed
        && this.getSelectedQuantityCount() > 0
        && !this.cartDrawerIsOpen();

      this.stickyBar.classList.toggle('is-visible', shouldShow);
      this.stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    handleExternalCartInteraction(event) {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const cartRoot = target.closest(
        '#ea-sticky-cart-root, .ea-sticky-cart, cart-drawer, #CartDrawer, .cart-drawer'
      );

      if (!cartRoot) {
        return;
      }

      if (event.type === 'click' && !target.closest('button, a, [role="button"]')) {
        return;
      }

      this.scheduleDiscountReconciliation(850);
      window.setTimeout(() => this.updateStickyVisibility(), 80);
      window.setTimeout(() => this.updateStickyVisibility(), 320);
    }

    isCartMutationUrl(value) {
      try {
        const url = new URL(String(value || ''), window.location.origin);
        return /\/cart\/(add|change|update|clear)(\.js)?\/?$/i.test(url.pathname);
      } catch (error) {
        return false;
      }
    }

    observeCartRequests() {
      if (!('PerformanceObserver' in window)) {
        return;
      }

      try {
        this.cartRequestObserver = new PerformanceObserver((list) => {
          const cartWasChanged = list.getEntries().some((entry) => {
            return this.isCartMutationUrl(entry.name);
          });

          if (cartWasChanged) {
            this.scheduleDiscountReconciliation(220);
          }
        });

        this.cartRequestObserver.observe({
          type: 'resource',
          buffered: false
        });
      } catch (error) {
        this.cartRequestObserver = null;
      }
    }

    observeCartDrawer() {
      if (!('MutationObserver' in window) || !document.body) {
        return;
      }

      const cartSelector = '#ea-sticky-cart-root, .ea-sticky-cart, cart-drawer, #CartDrawer, .cart-drawer';

      this.cartDomObserver = new MutationObserver((mutations) => {
        const cartWasRendered = mutations.some((mutation) => {
          const target = mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;

          if (target && target.closest(cartSelector)) {
            return true;
          }

          return Array.from(mutation.addedNodes).some((node) => (
            node instanceof Element
            && (
              node.matches(cartSelector)
              || Boolean(node.querySelector(cartSelector))
            )
          ));
        });

        if (cartWasRendered) {
          this.scheduleDiscountReconciliation(320);
          this.updateStickyVisibility();
          window.setTimeout(() => this.updateStickyVisibility(), 180);
        }
      });

      this.cartDomObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'open', 'aria-hidden', 'style']
      });
    }

    handleChange(event) {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const card = target.closest('[data-wm-product-card]');

      if (!card || !this.root.contains(card)) {
        return;
      }

      const productId = String(card.dataset.productId || '');
      const state = this.products.get(productId);

      if (!state) {
        return;
      }

      if (target.matches('[data-wm-variant-select]')) {
        this.updateCard(state);

        if (state.checkbox.checked) {
          this.selected.set(state.id, state);
          this.renderSelected();
        }
      }

      if (target.matches('[data-wm-select]')) {
        if (target.checked) {
          const variant = this.getVariant(state);

          if (!variant || !variant.available) {
            target.checked = false;
            return;
          }
        }

        this.setStateSelected(state, target.checked);
        this.clearStatus();
        this.renderSelected();
      }
    }

    handleClick(event) {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const quantityButton = target.closest('[data-wm-quantity-action]');

      if (quantityButton && this.root.contains(quantityButton)) {
        const state = this.products.get(
          String(quantityButton.dataset.productId || '')
        );

        const action = String(
          quantityButton.dataset.wmQuantityAction || ''
        );

        if (!state) {
          return;
        }

        this.adjustQuantity(
          state,
          action === 'increase' ? 1 : -1
        );

        return;
      }

      const removeButton = target.closest('[data-wm-remove]');

      if (!removeButton || !this.root.contains(removeButton)) {
        return;
      }

      const state = this.products.get(
        String(removeButton.dataset.productId || '')
      );

      if (!state) {
        return;
      }

      this.setStateSelected(state, false);
      this.clearStatus();
      this.renderSelected();
      state.checkbox.focus({ preventScroll: true });
    }

    createQuantityStepper(state, context) {
      const stepper = document.createElement('div');
      stepper.className = `wm-set-builder__quantity-stepper wm-set-builder__quantity-stepper--${context}`;

      const decrease = document.createElement('button');
      decrease.type = 'button';
      decrease.className = 'wm-set-builder__quantity-button';
      decrease.dataset.wmQuantityAction = 'decrease';
      decrease.dataset.productId = state.id;
      decrease.setAttribute(
        'aria-label',
        `${state.title}: Stückzahl verringern`
      );
      decrease.textContent = '−';

      const value = document.createElement('span');
      value.className = 'wm-set-builder__quantity-value';
      value.dataset.wmQuantityValue = '';
      value.setAttribute('aria-live', 'polite');
      value.textContent = String(state.quantity);

      const increase = document.createElement('button');
      increase.type = 'button';
      increase.className = 'wm-set-builder__quantity-button';
      increase.dataset.wmQuantityAction = 'increase';
      increase.dataset.productId = state.id;
      increase.setAttribute(
        'aria-label',
        `${state.title}: Stückzahl erhöhen`
      );
      increase.textContent = '+';

      stepper.append(decrease, value, increase);

      return {
        element: stepper,
        value
      };
    }

    setStateSelected(state, selected) {
      state.checkbox.checked = selected;
      state.card.classList.toggle('is-selected', selected);

      if (selected) {
        state.quantity = Math.max(1, toInteger(state.quantity, 1));
        this.selected.set(state.id, state);
      } else {
        this.selected.delete(state.id);
        state.quantity = 1;
      }

      if (state.checkText) {
        state.checkText.textContent = selected
          ? 'Im Set ausgewählt'
          : 'Zum Set hinzufügen';
      }

      this.updateSelectionVisual(state, selected);
      this.updateQuantityUI(state);
    }

    adjustQuantity(state, change) {
      if (!state.checkbox.checked) {
        if (change > 0) {
          this.setStateSelected(state, true);
        }
      } else {
        const nextQuantity = Math.min(
          99,
          Math.max(
            0,
            toInteger(state.quantity, 1) + change
          )
        );

        if (nextQuantity === 0) {
          this.setStateSelected(state, false);
        } else {
          state.quantity = nextQuantity;
          this.selected.set(state.id, state);
          this.updateQuantityUI(state);
        }
      }

      this.clearStatus();
      this.renderSelected();
    }

    updateQuantityUI(state) {
      if (state.cardQuantityControls) {
        state.cardQuantityControls.hidden = !state.checkbox.checked;
      }

      if (state.cardQuantityValue) {
        state.cardQuantityValue.textContent = String(state.quantity);
      }
    }

    getSelectedQuantityCount() {
      let quantity = 0;

      this.selected.forEach((state) => {
        quantity += Math.max(
          1,
          toInteger(state.quantity, 1)
        );
      });

      return quantity;
    }

    getVariant(state) {
      const selectedId = state.select
        ? toInteger(state.select.value)
        : toInteger(
            state.variants[0]
            && state.variants[0].id
          );

      return state.variants.find(
        (variant) => variant.id === selectedId
      ) || state.variants.find(
        (variant) => variant.available
      ) || state.variants[0];
    }

    updateCard(state) {
      const variant = this.getVariant(state);

      if (!variant) {
        return;
      }

      if (state.price) {
        state.price.textContent = this.formatMoney(variant.price);
      }

      if (state.compare) {
        const showCompare = variant.compareAtPrice > variant.price;
        state.compare.hidden = !showCompare;
        state.compare.textContent = showCompare
          ? this.formatMoney(variant.compareAtPrice)
          : '';
      }

      if (state.image && variant.image) {
        state.image.src = variant.image;
        state.image.alt = variant.imageAlt;
        state.image.removeAttribute('srcset');
      }

      state.checkbox.disabled = !variant.available;

      if (!variant.available && state.checkbox.checked) {
        this.setStateSelected(state, false);
      }

      this.updateSelectionVisual(state, state.checkbox.checked);
      this.updateQuantityUI(state);
    }

    setImportant(element, property, value) {
      if (
        element instanceof HTMLElement
        || element instanceof SVGElement
      ) {
        element.style.setProperty(
          property,
          value,
          'important'
        );
      }
    }

    forceCardColors(state) {
      const ink = '#22282b';
      const muted = '#687076';
      const surface = '#fffdfa';

      this.setImportant(state.card, 'background-color', surface);
      this.setImportant(state.card, 'color', ink);
      this.setImportant(state.card, '-webkit-text-fill-color', ink);
      this.setImportant(state.card, 'opacity', '1');

      const content = state.card.querySelector(
        '.wm-set-builder__product-content'
      );

      this.setImportant(content, 'background-color', surface);
      this.setImportant(content, 'color', ink);
      this.setImportant(content, '-webkit-text-fill-color', ink);
      this.setImportant(content, 'opacity', '1');

      state.card.querySelectorAll(
        '.wm-set-builder__product-title, .wm-set-builder__product-title a, .wm-set-builder__product-price, [data-wm-card-price], .wm-set-builder__variant-label, .wm-set-builder__variant-select'
      ).forEach((element) => {
        this.setImportant(element, 'color', ink);
        this.setImportant(element, '-webkit-text-fill-color', ink);
        this.setImportant(element, 'opacity', '1');
        this.setImportant(element, 'visibility', 'visible');
        this.setImportant(element, 'text-shadow', 'none');
        this.setImportant(element, 'mix-blend-mode', 'normal');
      });

      if (state.compare) {
        this.setImportant(state.compare, 'color', muted);
        this.setImportant(state.compare, '-webkit-text-fill-color', muted);
        this.setImportant(state.compare, 'opacity', '1');
      }

      const chip = state.card.querySelector(
        '.wm-set-builder__collection-chip'
      );

      this.setImportant(chip, 'color', '#ffffff');
      this.setImportant(chip, '-webkit-text-fill-color', '#ffffff');
      this.setImportant(chip, 'opacity', '1');

      if (state.cardQuantityControls) {
        this.setImportant(state.cardQuantityControls, 'color', ink);
        this.setImportant(
          state.cardQuantityControls,
          '-webkit-text-fill-color',
          ink
        );
        this.setImportant(state.cardQuantityControls, 'opacity', '1');
      }
    }

    updateSelectionVisual(state, selected) {
      const ink = '#22282b';
      const copper = '#b44f2d';
      const copyColor = selected ? '#ffffff' : ink;

      this.setImportant(
        state.checkLabel,
        'background-color',
        selected ? copper : 'transparent'
      );

      this.setImportant(
        state.checkLabel,
        'border-color',
        selected ? copper : '#343b3f'
      );

      this.setImportant(state.checkLabel, 'color', copyColor);
      this.setImportant(
        state.checkLabel,
        '-webkit-text-fill-color',
        copyColor
      );

      this.setImportant(state.checkText, 'color', copyColor);
      this.setImportant(
        state.checkText,
        '-webkit-text-fill-color',
        copyColor
      );
      this.setImportant(state.checkText, 'opacity', '1');

      this.setImportant(
        state.checkBox,
        'background-color',
        selected
          ? 'rgba(255, 255, 255, 0.12)'
          : '#ffffff'
      );

      this.setImportant(
        state.checkBox,
        'border-color',
        selected
          ? 'rgba(255, 255, 255, 0.78)'
          : '#8d9294'
      );

      this.setImportant(
        state.checkBox,
        'color',
        selected ? '#ffffff' : 'transparent'
      );

      this.setImportant(
        state.checkBox,
        '-webkit-text-fill-color',
        selected ? '#ffffff' : 'transparent'
      );

      if (state.checkIcon) {
        this.setImportant(
          state.checkIcon,
          'opacity',
          selected ? '1' : '0'
        );

        this.setImportant(
          state.checkIcon,
          'visibility',
          selected ? 'visible' : 'hidden'
        );

        this.setImportant(
          state.checkIcon,
          'color',
          selected ? '#ffffff' : 'transparent'
        );

        state.checkIcon.querySelectorAll('path').forEach((path) => {
          this.setImportant(
            path,
            'stroke',
            selected ? '#ffffff' : 'transparent'
          );

          this.setImportant(
            path,
            'opacity',
            selected ? '1' : '0'
          );

          this.setImportant(
            path,
            'visibility',
            selected ? 'visible' : 'hidden'
          );
        });
      }

      this.updateQuantityUI(state);
    }

    renderSelected() {
      const list = this.elements.selectionList;

      if (!list) {
        this.renderSummary();
        return;
      }

      list.replaceChildren();

      const entries = Array.from(this.selected.values());

      entries.forEach((state, index) => {
        const variant = this.getVariant(state);

        if (!variant) {
          return;
        }

        if (index > 0) {
          const plus = document.createElement('span');
          plus.className = 'wm-set-builder__plus';
          plus.setAttribute('aria-hidden', 'true');
          plus.textContent = '+';
          list.append(plus);
        }

        const item = document.createElement('div');
        item.className = 'wm-set-builder__selected-item';
        item.setAttribute('role', 'listitem');

        if (variant.image) {
          const image = document.createElement('img');
          image.className = 'wm-set-builder__selected-image';
          image.src = variant.image;
          image.alt = variant.imageAlt;
          image.loading = 'lazy';
          item.append(image);
        } else {
          const imagePlaceholder = document.createElement('span');
          imagePlaceholder.className = 'wm-set-builder__selected-image';
          imagePlaceholder.setAttribute('aria-hidden', 'true');
          item.append(imagePlaceholder);
        }

        const copy = document.createElement('div');
        copy.className = 'wm-set-builder__selected-copy';

        const title = document.createElement('span');
        title.className = 'wm-set-builder__selected-title';
        title.textContent = state.title;
        copy.append(title);

        if (variant.title && variant.title !== 'Default Title') {
          const variantTitle = document.createElement('span');
          variantTitle.className = 'wm-set-builder__selected-variant';
          variantTitle.textContent = variant.title;
          copy.append(variantTitle);
        }

        const price = document.createElement('span');
        price.className = 'wm-set-builder__selected-price';

        price.textContent = state.quantity > 1
          ? `${state.quantity} × ${this.formatMoney(variant.price)} · ${this.formatMoney(variant.price * state.quantity)}`
          : this.formatMoney(variant.price);

        copy.append(price);
        item.append(copy);

        const selectedStepper = this.createQuantityStepper(
          state,
          'selection'
        );

        item.append(selectedStepper.element);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'wm-set-builder__selected-remove';
        remove.dataset.wmRemove = '';
        remove.dataset.productId = state.id;

        remove.setAttribute(
          'aria-label',
          `${state.title} aus dem Set entfernen`
        );

        remove.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="m5 5 10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"/></svg>';

        item.append(remove);
        list.append(item);
      });

      if (this.elements.selectionShell) {
        this.elements.selectionShell.hidden = entries.length === 0;
      }

      if (this.elements.selectionCount) {
        this.elements.selectionCount.textContent = String(
          this.getSelectedQuantityCount()
        );
      }

      this.renderSummary();
    }

    getGiftQuantity(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return 0;
      }

      const gift = this.config.gift || {};
      const productId = toInteger(gift.productId);
      const variantId = toInteger(gift.variantId);

      if (productId || variantId) {
        return cart.items.reduce((quantity, item) => {
          const isGift = (
            productId
            && toInteger(item.product_id) === productId
          ) || (
            variantId
            && toInteger(item.variant_id) === variantId
          );

          return isGift
            ? quantity + Math.max(0, toInteger(item.quantity))
            : quantity;
        }, 0);
      }

      if (gift.assumeOne && toInteger(cart.item_count) > 0) {
        return 1;
      }

      return 0;
    }

    getQualifyingCartCount(cart) {
      const itemCount = Math.max(
        0,
        toInteger(cart && cart.item_count)
      );

      return Math.max(
        0,
        itemCount - this.getGiftQuantity(cart)
      );
    }

    getTier(itemCount) {
      return this.tiers.reduce((activeTier, tier) => {
        return itemCount >= tier.minimum
          ? tier
          : activeTier;
      }, null);
    }

    getProjection() {
      const cart = this.cart || {};
      const gift = this.config.gift || {};

      const cartSubtotal = Math.max(
        0,
        toInteger(
          cart.original_total_price
          ?? cart.items_subtotal_price
          ?? cart.total_price,
          0
        )
      );

      const qualifyingCartCount = this.getQualifyingCartCount(cart);
      let selectedSubtotal = 0;

      this.selected.forEach((state) => {
        const variant = this.getVariant(state);
        const quantity = Math.max(
          1,
          toInteger(state.quantity, 1)
        );

        selectedSubtotal += variant
          ? variant.price * quantity
          : 0;
      });

      const giftIsSelected = Array.from(
        this.selected.values()
      ).some((state) => {
        const variant = this.getVariant(state);

        return variant
          && variant.id === toInteger(gift.variantId);
      });

      const giftWillBeAdded = Boolean(
        gift.ensure
        && toInteger(gift.variantId)
        && this.getGiftQuantity(cart) === 0
        && this.selected.size > 0
        && !giftIsSelected
      );

      const projectedSubtotal = cartSubtotal
        + selectedSubtotal
        + (
          giftWillBeAdded
            ? Math.max(0, toInteger(gift.price))
            : 0
        );

      const qualifyingCount = qualifyingCartCount
        + this.getSelectedQuantityCount();

      const tier = this.getTier(qualifyingCount);

      const saving = tier
        ? Math.round(
            projectedSubtotal * tier.percent / 100
          )
        : 0;

      return {
        projectedSubtotal,
        qualifyingCartCount,
        qualifyingCount,
        tier,
        saving,
        total: Math.max(
          0,
          projectedSubtotal - saving
        )
      };
    }

    getTierDisplayName(tier) {
      if (!tier) {
        return 'Set zusammenstellen';
      }

      if (tier.key === 'duo' || tier.minimum === 2) {
        return 'Dein Duo-Vorteil';
      }

      if (tier.key === 'trio' || tier.minimum === 3) {
        return 'Dein Trio-Vorteil';
      }

      if (tier.key === 'complete' || tier.minimum >= 4) {
        return 'Dein Komplett-Vorteil';
      }

      return String(tier.name || 'Dein Setvorteil');
    }

    updateTierRail(projection) {
      if (!this.tierRail) {
        return;
      }

      const steps = Array.from(
        this.tierRail.querySelectorAll(
          '[data-wm-tier-minimum]'
        )
      );

      let currentIndex = -1;

      steps.forEach((step, index) => {
        const minimum = Math.max(
          1,
          toInteger(
            step.dataset.wmTierMinimum,
            1
          )
        );

        const reached = projection.qualifyingCount >= minimum;

        if (reached) {
          currentIndex = index;
        }

        step.classList.toggle('is-reached', reached);
        step.classList.remove('is-current', 'is-next');
        step.removeAttribute('aria-current');
      });

      if (currentIndex >= 0 && steps[currentIndex]) {
        steps[currentIndex].classList.add('is-current');
        steps[currentIndex].setAttribute('aria-current', 'step');
      } else if (steps[0]) {
        steps[0].classList.add('is-next');
      }

      const nextStep = steps[currentIndex + 1];

      if (nextStep) {
        nextStep.classList.add('is-next');
      }

      const railSpan = steps.length > 0
        ? (steps.length - 1) / steps.length * 100
        : 0;

      const progress = steps.length > 1 && currentIndex > 0
        ? currentIndex / (steps.length - 1) * railSpan
        : 0;

      this.tierRail.style.setProperty(
        '--wm-tier-progress',
        `${progress}%`
      );
    }

    updateStickyBar(projection, selectedCount, isSinglePurchase) {
      if (!this.stickyBar) {
        return;
      }

      if (this.stickyCount) {
        this.stickyCount.textContent = selectedCount === 1
          ? '1 Produkt'
          : `${selectedCount} Produkte`;
      }

      if (this.stickySaving) {
        this.stickySaving.textContent = projection.saving > 0
          ? `${this.formatMoney(projection.saving)} Vorteil`
          : 'Ohne Setvorteil';
      }

      if (this.stickyTotal) {
        this.stickyTotal.textContent = this.formatMoney(
          projection.total
        );
      }

      if (this.stickySubmit) {
        this.stickySubmit.disabled = this.busy
          || selectedCount === 0;

        this.stickySubmit.textContent = this.busy
          ? 'WIRD VORBEREITET …'
          : isSinglePurchase
            ? 'EINZELN BESTELLEN'
            : 'SET BESTELLEN';
      }

      this.stickyBar.classList.toggle(
        'is-busy',
        this.busy
      );

      this.updateStickyVisibility();
    }

    renderSummary() {
      const projection = this.getProjection();
      const selectedCount = this.getSelectedQuantityCount();

      const isSinglePurchase = selectedCount === 1
        && !projection.tier;

      const totalLabel = this.elements.totalLabel
        || this.root.querySelector('[data-wm-total-label]')
        || this.root.querySelector(
          '.wm-set-builder__summary-total > span'
        );

      const fineprint = this.elements.fineprint
        || this.root.querySelector('[data-wm-fineprint]')
        || this.root.querySelector(
          '.wm-set-builder__fineprint'
        );

      if (this.elements.subtotal) {
        this.elements.subtotal.textContent = this.formatMoney(
          projection.projectedSubtotal
        );
      }

      if (this.elements.saving) {
        this.elements.saving.textContent = projection.saving > 0
          ? `−${this.formatMoney(projection.saving)}`
          : this.formatMoney(0);
      }

      if (this.elements.total) {
        this.elements.total.textContent = this.formatMoney(
          projection.total
        );
      }

      if (totalLabel) {
        totalLabel.textContent = isSinglePurchase
          ? 'Dein Preis'
          : 'Dein Setpreis';
      }

      if (this.elements.tierName) {
        this.elements.tierName.textContent = projection.tier
          ? this.getTierDisplayName(projection.tier)
          : isSinglePurchase
            ? 'Einzelbestellung'
            : 'Set zusammenstellen';
      }

      if (this.elements.progress) {
        this.elements.progress.textContent = this.getProgressText(
          projection
        );
      }

      if (this.elements.cartNote) {
        const count = projection.qualifyingCartCount;

        this.elements.cartNote.hidden = count === 0;

        this.elements.cartNote.textContent = count === 1
          ? '1 vorhandener Artikel im Warenkorb wird mitgerechnet.'
          : `${count} vorhandene Artikel im Warenkorb werden mitgerechnet.`;
      }

      if (this.elements.submit) {
        this.elements.submit.disabled = Boolean(
          this.busy
          || selectedCount === 0
        );
      }

      if (this.elements.buttonLabel) {
        this.elements.buttonLabel.textContent = this.busy
          ? isSinglePurchase
            ? 'PRODUKT WIRD HINZUGEFÜGT …'
            : 'SET WIRD VORBEREITET …'
          : isSinglePurchase
            ? 'OHNE SETVORTEIL BESTELLEN'
            : String(
                this.config.buttonLabel
                || 'MEIN SET IN DEN WARENKORB'
              );
      }

      if (fineprint) {
        fineprint.textContent = isSinglePurchase
          ? 'Einzelkauf ohne Setvorteil. Ab zwei Produkten wird der passende Vorteil automatisch aktiviert.'
          : 'Der passende Setvorteil wird automatisch im Warenkorb hinterlegt.';
      }

      this.updateTierRail(projection);

      this.updateStickyBar(
        projection,
        selectedCount,
        isSinglePurchase
      );
    }

    getProgressText(projection) {
      const count = projection.qualifyingCount;

      if (!this.tiers.length) {
        return 'Die Rabattstaffel ist noch nicht eingerichtet.';
      }

      const firstTier = this.tiers[0];
      const lastTier = this.tiers[this.tiers.length - 1];

      if (count < firstTier.minimum) {
        const missing = firstTier.minimum - count;

        return missing === 1
          ? 'Noch 1 Produkt bis zu deinem Vorteil.'
          : `Wähle noch ${missing} Produkte für deinen Vorteil.`;
      }

      if (count >= lastTier.minimum) {
        return 'Dein stärkster Setvorteil ist erreicht.';
      }

      const nextTier = this.tiers.find(
        (tier) => tier.minimum > count
      );

      const missing = nextTier
        ? nextTier.minimum - count
        : 0;

      return missing === 1
        ? 'Noch 1 Produkt – dann wächst dein Vorteil.'
        : `Noch ${missing} Produkte bis zum nächsten Vorteil.`;
    }

    getCartDiscountCodes(cart) {
      const codes = [];

      if (Array.isArray(cart && cart.discount_codes)) {
        cart.discount_codes.forEach((entry) => {
          const code = typeof entry === 'string'
            ? entry
            : entry && entry.code;

          if (code) {
            codes.push(normalizeCode(code));
          }
        });
      }

      if (
        Array.isArray(
          cart
          && cart.cart_level_discount_applications
        )
      ) {
        cart.cart_level_discount_applications.forEach(
          (application) => {
            const details = application
              && application.discount_application;

            const title = (
              application
              && application.title
            ) || (
              details
              && details.title
            );

            if (title) {
              codes.push(normalizeCode(title));
            }
          }
        );
      }

      return Array.from(new Set(codes));
    }

    hasManagedDiscountReference(cart) {
      const tierCodes = new Set(
        this.tiers
          .map((tier) => normalizeCode(tier.code))
          .filter(Boolean)
      );

      return this.getCartDiscountCodes(cart).some(
        (code) => tierCodes.has(code)
      );
    }

    hasSetCartMarker(cart) {
      const attributes = cart && cart.attributes;

      return Boolean(
        attributes
        && String(
          attributes[SET_CART_ATTRIBUTE] || ''
        ) === 'active'
      );
    }

    shouldManageCartDiscount(cart) {
      return this.hasSetCartMarker(cart)
        || this.hasManagedDiscountReference(cart);
    }

    scheduleDiscountReconciliation(delay = 700) {
      const now = Date.now();

      const ignoreFor = Math.max(
        0,
        this.ignoreCartEventsUntil - now
      );

      const retryFor = this.busy || this.discountSyncing
        ? 300
        : 0;

      const effectiveDelay = Math.max(
        delay,
        ignoreFor ? ignoreFor + 80 : 0,
        retryFor
      );

      window.clearTimeout(this.discountSyncTimer);

      this.discountSyncTimer = window.setTimeout(() => {
        if (
          this.busy
          || this.discountSyncing
          || Date.now() < this.ignoreCartEventsUntil
        ) {
          this.scheduleDiscountReconciliation(250);
          return;
        }

        this.reconcileCartDiscount();
      }, effectiveDelay);
    }

    async reconcileCartDiscount() {
      if (this.busy || this.discountSyncing) {
        return;
      }

      this.discountSyncing = true;

      try {
        const currentCart = await this.request(
          'cart.js',
          {
            cache: 'no-store'
          }
        );

        if (!this.shouldManageCartDiscount(currentCart)) {
          this.cart = currentCart;
          this.renderSummary();
          return;
        }

        const qualifyingCount = this.getQualifyingCartCount(
          currentCart
        );

        const tier = this.getTier(qualifyingCount);

        const tierIsEligible = Boolean(
          tier
          && tier.code
          && toInteger(currentCart.item_count) >= tier.cartMinimum
        );

        const desiredCode = tierIsEligible
          ? String(tier.code).trim()
          : '';

        const managedCodes = new Set(
          this.tiers
            .map((entry) => normalizeCode(entry.code))
            .filter(Boolean)
        );

        const currentManagedCodes = this
          .getCartDiscountCodes(currentCart)
          .filter((code) => managedCodes.has(code));

        const cartIsEmpty = toInteger(currentCart.item_count) === 0
          || qualifyingCount === 0;

        const keepMarker = !cartIsEmpty;
        const markerIsActive = this.hasSetCartMarker(currentCart);

        const desiredIsApplied = desiredCode
          ? this.discountIsApplied(currentCart, desiredCode)
          : false;

        const hasWrongManagedCode = desiredCode
          ? currentManagedCodes.some(
              (code) => code !== normalizeCode(desiredCode)
            )
          : currentManagedCodes.length > 0;

        const discountNeedsUpdate = desiredCode
          ? !desiredIsApplied || hasWrongManagedCode
          : hasWrongManagedCode;

        const markerNeedsUpdate = markerIsActive !== keepMarker;

        if (!discountNeedsUpdate && !markerNeedsUpdate) {
          this.cart = currentCart;
          this.renderSummary();
          return;
        }

        let updatedCart = await this.request(
          'cart/update.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              discount: desiredCode,
              attributes: {
                [SET_CART_ATTRIBUTE]: keepMarker
                  ? 'active'
                  : ''
              }
            })
          }
        );

        if (
          desiredCode
          && !this.discountIsApplied(updatedCart, desiredCode)
        ) {
          await new Promise((resolve) => {
            window.setTimeout(resolve, 180);
          });

          updatedCart = await this.request(
            'cart.js',
            {
              cache: 'no-store'
            }
          );

          if (
            !this.discountIsApplied(
              updatedCart,
              desiredCode
            )
          ) {
            throw new Error(
              `Shopify konnte ${desiredCode} nach der Mengenänderung nicht anwenden.`
            );
          }
        }

        this.cart = updatedCart;
        this.renderSummary();
        this.refreshStickyCart(updatedCart);
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : 'Der Setvorteil konnte nicht aktualisiert werden.';

        this.setStatus(message, 'error');
      } finally {
        this.discountSyncing = false;
      }
    }

    async refreshCart() {
      if (this.busy || this.redirecting) {
        return;
      }

      try {
        this.cart = await this.request('cart.js');
        this.renderSummary();

        if (this.shouldManageCartDiscount(this.cart)) {
          this.scheduleDiscountReconciliation(350);
        }
      } catch (error) {
        this.setStatus(
          'Der Warenkorb konnte gerade nicht geladen werden. Beim Bestellen versuchen wir es erneut.',
          'error'
        );
      }
    }

    async submit() {
      if (this.busy || this.selected.size === 0) {
        return;
      }

      let itemsCommitted = false;

      this.setBusy(true);
      this.clearStatus();

      try {
        const cartBefore = await this.request('cart.js');
        const items = [];

        this.selected.forEach((state) => {
          const variant = this.getVariant(state);

          if (!variant || !variant.available) {
            throw new Error(
              `${state.title} ist in der gewählten Ausführung nicht mehr verfügbar.`
            );
          }

          items.push({
            id: variant.id,
            quantity: Math.max(
              1,
              toInteger(state.quantity, 1)
            )
          });
        });

        const gift = this.config.gift || {};
        const giftVariantId = toInteger(gift.variantId);

        const giftAlreadyIncluded = items.some(
          (item) => item.id === giftVariantId
        );

        if (
          gift.ensure
          && giftVariantId
          && this.getGiftQuantity(cartBefore) === 0
          && !giftAlreadyIncluded
        ) {
          items.push({
            id: giftVariantId,
            quantity: 1
          });
        }

        if (!items.length) {
          throw new Error(
            'Bitte wähle zuerst deine Produkte aus.'
          );
        }

        await this.request(
          'cart/add.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items })
          }
        );

        itemsCommitted = true;

        const cartWithItems = await this.request('cart.js');
        const qualifyingCount = this.getQualifyingCartCount(
          cartWithItems
        );

        const tier = this.getTier(qualifyingCount);

        if (!tier) {
          const individualUpdate = {
            attributes: {
              [SET_CART_ATTRIBUTE]: 'active'
            }
          };

          if (this.hasManagedDiscountReference(cartWithItems)) {
            individualUpdate.discount = '';
          }

          let individualCart = cartWithItems;

          try {
            individualCart = await this.request(
              'cart/update.js',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(individualUpdate)
              }
            );
          } catch (markerError) {
            individualCart = cartWithItems;
          }

          this.cart = individualCart;
          this.clearSelection();

          this.setStatus(
            'Dein Produkt liegt ohne Setvorteil im Warenkorb. Ab zwei Set-Produkten wird der passende Vorteil automatisch aktiviert.',
            'success'
          );

          this.setBusy(false);
          this.refreshStickyCart(individualCart);
          window[GLOBAL_NAME].refreshAll(this);

          return;
        }

        if (!tier.code) {
          throw new Error(
            `Der Rabattcode für ${tier.name} ist noch nicht eingerichtet.`
          );
        }

        if (
          toInteger(cartWithItems.item_count) < tier.cartMinimum
        ) {
          throw new Error(
            'Das Pflegeöl fehlt noch im Warenkorb. Der Setvorteil konnte deshalb nicht aktiviert werden.'
          );
        }

        const discountedCart = await this.request(
          'cart/update.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              discount: tier.code,
              attributes: {
                [SET_CART_ATTRIBUTE]: 'active'
              }
            })
          }
        );

        if (
          !this.discountIsApplied(
            discountedCart,
            tier.code
          )
        ) {
          throw new Error(
            `Shopify hat ${tier.code} nicht angenommen. Das Gratis-Öl zählt bei Shopify nicht zur Mindestartikelmenge; erforderlich sind ${tier.minimum} bezahlte Set-Produkte.`
          );
        }

        const verifiedCart = await this.request(
          'cart.js',
          {
            cache: 'no-store'
          }
        );

        if (
          !this.discountIsApplied(
            verifiedCart,
            tier.code
          )
        ) {
          throw new Error(
            'Der Setvorteil wurde nicht dauerhaft im Warenkorb gespeichert.'
          );
        }

        this.cart = verifiedCart;
        this.clearSelection();

        this.setStatus(
          'Dein Set liegt mit bestätigtem Setvorteil im Warenkorb.',
          'success'
        );

        this.setBusy(false);
        this.refreshStickyCart(verifiedCart);
        window[GLOBAL_NAME].refreshAll(this);

        return;
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : 'Das Set konnte nicht hinzugefügt werden.';

        if (itemsCommitted) {
          try {
            this.cart = await this.request(
              'cart.js',
              {
                cache: 'no-store'
              }
            );
          } catch (cartError) {
            this.cart = this.cart || {};
          }

          this.clearSelection();

          this.setStatus(
            `${message} Die Produkte wurden hinzugefügt, aber wir öffnen den Warenkorb bewusst nicht ohne bestätigten Setvorteil.`,
            'error'
          );
        } else {
          this.setStatus(message, 'error');
        }
      } finally {
        if (!this.redirecting && this.busy) {
          this.setBusy(false);
        }
      }
    }

    discountIsApplied(cart, code) {
      const targetCode = normalizeCode(code);

      if (Array.isArray(cart && cart.discount_codes)) {
        const codeIsApplied = cart.discount_codes.some((entry) => {
          const entryCode = typeof entry === 'string'
            ? entry
            : entry && entry.code;

          const applicable = (
            typeof entry === 'object'
            && entry !== null
          )
            ? entry.applicable !== false
            : true;

          return normalizeCode(entryCode) === targetCode
            && applicable;
        });

        if (codeIsApplied) {
          return true;
        }
      }

      if (
        Array.isArray(
          cart
          && cart.cart_level_discount_applications
        )
        && cart.cart_level_discount_applications.length
      ) {
        return cart.cart_level_discount_applications.some(
          (application) => {
            const details = application
              && application.discount_application;

            const title = (
              application
              && application.title
            ) || (
              details
              && details.title
            );

            return normalizeCode(title) === targetCode;
          }
        );
      }

      return false;
    }

    clearSelection() {
      this.selected.forEach((state) => {
        state.checkbox.checked = false;
        state.card.classList.remove('is-selected');
        state.quantity = 1;

        if (state.checkText) {
          state.checkText.textContent = 'Zum Set hinzufügen';
        }

        this.updateSelectionVisual(state, false);
        this.updateQuantityUI(state);
      });

      this.selected.clear();
      this.renderSelected();
    }

    setBusy(isBusy) {
      this.busy = isBusy;

      if (this.elements.spinner) {
        this.elements.spinner.hidden = !isBusy;
      }

      this.renderSummary();
    }

    setStatus(message, type = '') {
      if (!this.elements.status) {
        return;
      }

      this.elements.status.textContent = message;

      this.elements.status.classList.toggle(
        'is-error',
        type === 'error'
      );

      this.elements.status.classList.toggle(
        'is-success',
        type === 'success'
      );
    }

    clearStatus() {
      this.setStatus('');
    }

    refreshStickyCart(cart) {
      this.ignoreCartEventsUntil = Date.now() + 1400;

      const dispatchRefresh = () => {
        document.dispatchEvent(
          new CustomEvent(
            'cart:refresh',
            {
              bubbles: true,
              detail: cart
            }
          )
        );
      };

      dispatchRefresh();
      window.setTimeout(dispatchRefresh, 350);
    }

    endpoint(path) {
      const configuredRoot = this.config.rootUrl
        || (
          window.Shopify
          && window.Shopify.routes
          && window.Shopify.routes.root
        )
        || '/';

      const root = String(configuredRoot).endsWith('/')
        ? String(configuredRoot)
        : `${configuredRoot}/`;

      return `${root}${String(path).replace(/^\//, '')}`;
    }

    async request(path, options = {}) {
      let response;

      try {
        response = await fetch(
          this.endpoint(path),
          {
            credentials: 'same-origin',
            ...options,
            headers: {
              Accept: 'application/json',
              ...(options.headers || {})
            }
          }
        );
      } catch (error) {
        throw new Error(
          'Die Verbindung zum Warenkorb ist fehlgeschlagen. Bitte versuche es erneut.'
        );
      }

      let data;

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (!response.ok) {
        const description = data
          && (data.description || data.message);

        throw new Error(
          description
          || 'Shopify konnte den Warenkorb nicht aktualisieren.'
        );
      }

      return data || {};
    }

    formatMoney(cents) {
      const currency = (
        this.cart
        && this.cart.currency
      )
        || this.config.currency
        || (
          window.Shopify
          && window.Shopify.currency
          && window.Shopify.currency.active
        )
        || 'EUR';

      const locale = document.documentElement.lang
        || 'de-DE';

      try {
        return new Intl.NumberFormat(
          locale,
          {
            style: 'currency',
            currency
          }
        ).format(
          Math.max(0, toInteger(cents)) / 100
        );
      } catch (error) {
        return `${
          (
            Math.max(0, toInteger(cents)) / 100
          )
            .toFixed(2)
            .replace('.', ',')
        } €`;
      }
    }

    destroy() {
      window.clearTimeout(this.discountSyncTimer);

      if (this.cartDomObserver) {
        this.cartDomObserver.disconnect();
      }

      if (this.cartRequestObserver) {
        this.cartRequestObserver.disconnect();
      }

      if (this.summaryObserver) {
        this.summaryObserver.disconnect();
      }

      if (this.stickyBar) {
        this.stickyBar.remove();
        this.stickyBar = null;
      }

      this.controller.abort();
      activeInstances.delete(this);
      instances.delete(this.root);
      delete this.root.dataset.wmSetInitialized;
    }
  }

  const initWithin = (container = document) => {
    const roots = [];

    if (
      container instanceof Element
      && container.matches('[data-wm-set-builder]')
    ) {
      roots.push(container);
    }

    container
      .querySelectorAll('[data-wm-set-builder]')
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      const instance = new SetBuilder(root);
      instances.set(root, instance);
      activeInstances.add(instance);
      root.dataset.wmSetInitialized = 'true';
    });
  };

  const destroyWithin = (container) => {
    const roots = [];

    if (
      container instanceof Element
      && container.matches('[data-wm-set-builder]')
    ) {
      roots.push(container);
    }

    container
      .querySelectorAll('[data-wm-set-builder]')
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      const instance = instances.get(root);

      if (instance) {
        instance.destroy();
      }
    });
  };

  window[GLOBAL_NAME] = {
    initAll: () => initWithin(document),

    refreshAll: (exceptInstance = null) => {
      activeInstances.forEach((instance) => {
        if (instance !== exceptInstance) {
          instance.refreshCart();
        }
      });
    }
  };

  document.addEventListener(
    'shopify:section:load',
    (event) => initWithin(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => destroyWithin(event.target)
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initWithin(document),
      {
        once: true
      }
    );
  } else {
    initWithin(document);
  }
})();